/**
 * @description
 * Client billing tab component for managing billing and Stripe integration.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { HelpCircle, ExternalLink } from 'lucide-react';
import { ThresholdConfiguration } from '../../components/stripe/ThresholdConfiguration';
import { UsageAccumulatorWidget } from '../../components/stripe/UsageAccumulatorWidget';
import { StripeCustomerLink } from '../../components/stripe/StripeCustomerLink';
import { PricingConfiguration } from '../../components/stripe/PricingConfiguration';
import { SubscriptionManager } from '../../components/stripe/SubscriptionManager';
import { InvoiceList } from '../../components/stripe/InvoiceList';
import { PaymentSettings } from '../../components/stripe/PaymentSettings';
import { UsageDashboard } from '../../components/stripe/UsageDashboard';
import type { ClientBillingProps } from './types';

export function ClientBilling({ clientId }: ClientBillingProps) {
  return (
    <div className="space-y-6">
      {/* Billing Tab Header with Help */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Billing Management</h3>
        <a
          href="/docs/stripe/DOCUMENTATION.md"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          title="Open Stripe Billing Documentation"
        >
          <HelpCircle className="h-4 w-4" />
          <span>Help</span>
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* High visibility widgets at top */}
      <div className="grid gap-4 md:grid-cols-2">
        <UsageAccumulatorWidget clientId={clientId} />
        <ThresholdConfiguration clientId={clientId} />
      </div>

      {/* Stripe customer management */}
      <StripeCustomerLink clientId={clientId} />

      {/* Pricing and subscription management */}
      <div className="grid gap-6 md:grid-cols-2">
        <PricingConfiguration clientId={clientId} />
        <SubscriptionManager clientId={clientId} />
      </div>

      {/* Usage dashboard */}
      <UsageDashboard clientId={clientId} />

      {/* Invoices and payment settings */}
      <div className="grid gap-6 md:grid-cols-2">
        <InvoiceList clientId={clientId} showTypeFilter={true} />
        <PaymentSettings clientId={clientId} />
      </div>
    </div>
  );
}
